import { PaintBucket } from "lucide-react";
import ServicePage from "./ServicePage";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "../components/ui/carousel";
import { useServicePhotos } from "../hooks/useServicePhotos";

const basePhotos = [
  { src: "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/f8e401si_IMG_1151.jpeg", alt: "Glad stucwerk muren en plafond - Stukadoor Breukelen" },
  { src: "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/0k4pxmat_IMG_1152.jpeg", alt: "Stucwerk woonkamer met nissen - Cardoso Bouw" },
  { src: "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/rqj81ozb_IMG_1154.jpeg", alt: "Stukadoorswerk in uitvoering - Professioneel stucwerk" },
  { src: "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/9hr3mjnp_3902f7c4-02b6-4427-b91c-a0e000430543.jpeg", alt: "Muren voorbereiden voor stucwerk - Renovatie stucwerk" },
  { src: "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/59lgs2bz_881820b3-d0bf-4d60-a402-723f0ba7ba7d.jpeg", alt: "Wanden voor stukadoorswerk - Stucwerk Utrecht" },
];

const faqs = [
  {
    q: "Wat is het verschil tussen glad stucwerk en sierpleister?",
    a: "Glad pleisterwerk geeft een strakke, moderne wand die direct geschilderd of behangen kan worden. Sierpleister (zoals spachtelputz) heeft een decoratieve structuur, is stootvaster en verbergt kleine oneffenheden. Wij adviseren graag welke afwerking bij uw ruimte past.",
  },
  {
    q: "Hoe lang moet stucwerk drogen voordat ik kan schilderen?",
    a: "Afhankelijk van de laagdikte, temperatuur en ventilatie gemiddeld 1 tot 2 weken. Te vroeg schilderen kan leiden tot vlekken of loslatende verf. Wij laten u weten wanneer de wand klaar is om te schilderen.",
  },
  {
    q: "Kunnen oude, beschadigde muren opnieuw gestuct worden?",
    a: "Ja. Wij repareren scheuren en gaten, verwijderen loszittend stucwerk, brengen waar nodig een voorstrijk of wapeningsgaas aan en stucen de wand daarna weer strak en glad.",
  },
  {
    q: "Stucen jullie ook plafonds?",
    a: "Ja, wij stucen zowel wanden als plafonds, glad of met spachtelputz. Ook het wegwerken van oude structuurverf, granol of beschadigd plafondstucwerk behoort tot onze werkzaamheden.",
  },
  {
    q: "Doen jullie ook buitenstucwerk?",
    a: "Ja, wij verzorgen gevelstucwerk met speciaal buitenstuc dat bestand is tegen regen, vorst en UV-straling. Een gestucte gevel geeft uw woning een frisse, moderne uitstraling en extra bescherming.",
  },
  {
    q: "Hoeveel stof en overlast geeft stukadoorswerk?",
    a: "Wij dekken vloeren, kozijnen en meubels zorgvuldig af en laten de ruimte netjes achter. Stucwerk zelf geeft relatief weinig stof; vooral het drogen vraagt tijd. Wij plannen de werkzaamheden zo dat u zo min mogelijk overlast ervaart.",
  },
];

function StukadoorCarousel() {
  const photos = useServicePhotos("stukadoor", basePhotos);
  
  return (
    <div className="px-12" data-testid="stukadoor-carousel">
      <Carousel opts={{ loop: true }} className="w-full">
        <CarouselContent>
          {photos.map((photo, i) => (
            <CarouselItem key={i}>
              <div className="overflow-hidden">
                <img
                  src={photo.src}
                  alt={photo.alt}
                  className="w-full h-[400px] object-cover"
                />
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious className="bg-[#005F5F] text-white border-none hover:bg-[#C5A059] hover:text-white" />
        <CarouselNext className="bg-[#005F5F] text-white border-none hover:bg-[#C5A059] hover:text-white" />
      </Carousel>
    </div>
  );
}

export default function StukadoorPage() {
  return (
    <ServicePage
      seoTitle="Stukadoor Breukelen | Professioneel Stucwerk - Cardoso Bouw"
      seoDescription="Professioneel stukadoor werk in Breukelen. Glad pleisterwerk, sierpleister, renovatie stucwerk voor muren en plafonds. 8+ jaar ervaring. Vraag offerte aan."
      seoKeywords="stukadoor Breukelen, stucwerk Breukelen, muren stucen Breukelen, plafond stucen, stukadoor Hilversum, sierpleister, glad pleisterwerk, stukadoor Loosdrecht"
      title="Stukadoor"
      subtitle="Stukadoor Breukelen"
      heroImage="https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/f8e401si_IMG_1151.jpeg"
      icon={PaintBucket}
      description="Op zoek naar een professionele stukadoor in Breukelen? Cardoso Bouw en Onderhoud biedt hoogwaardig stukadoorswerk voor zowel nieuwbouw als renovatieprojecten. Onze ervaren stukadoors zorgen voor strakke muren en plafonds met een perfecte afwerking die direct klaar is om te schilderen of te behangen."
      features={[
        "Muren glad stucen",
        "Plafonds stucen",
        "Renovatie stucwerk",
        "Buitenstucwerk en gevels",
        "Sierpleister en decoratief stucwerk",
        "Spachtelputz afwerking",
        "Glad pleisterwerk",
        "Reparatie bestaand stucwerk",
      ]}
      faqs={faqs}
      extraContent={
        <div className="space-y-8">
          {/* PROJECT CAROUSEL */}
          <div>
            <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-[#0F172A] mb-6">
              Onze stucwerk projecten
            </h2>
            <StukadoorCarousel />
          </div>

          <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-[#0F172A]">
            Strakke afwerking gegarandeerd
          </h2>
          <p className="text-slate-600 text-base leading-relaxed">
            Stucwerk is de basis voor een mooie afwerking van uw interieur. Of het nu gaat om 
            nieuwbouw muren die voor het eerst gestuct moeten worden of om renovatiewerk waarbij 
            oude muren een nieuwe laag krijgen, onze stukadoors leveren altijd een vlak en strak 
            resultaat. Wij werken met hoogwaardige materialen van bekende merken voor een duurzaam 
            resultaat dat jarenlang meegaat.
          </p>

          <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-[#0F172A]">
            Verschillende soorten stucwerk
          </h3>
          <p className="text-slate-600 text-base leading-relaxed">
            Bij Cardoso Bouw en Onderhoud kunt u terecht voor diverse soorten stucwerk. 
            Glad pleisterwerk is de populairste keuze voor een moderne, strakke uitstraling. 
            Sierpleister biedt een decoratieve afwerking met structuur. Spachtelputz is ideaal 
            voor plafonds en geeft een subtiele textuur. Wij adviseren u graag over de beste 
            optie voor uw situatie en budget.
          </p>

          <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-[#0F172A]">
            Buitenstucwerk
          </h3>
          <p className="text-slate-600 text-base leading-relaxed">
            Naast binnenstucwerk verzorgen wij ook professioneel buitenstucwerk. Een gestucte 
            gevel geeft uw woning een frisse, moderne uitstraling en beschermt tegelijkertijd 
            tegen weersinvloeden. Wij gebruiken speciaal buitenstuc dat bestand is tegen regen, 
            vorst en UV-straling.
          </p>

          <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-[#0F172A]">
            Stukadoor in uw regio
          </h3>
          <p className="text-slate-600 text-base leading-relaxed">
            Wij voeren stukadoorswerk uit in heel Noord-Holland, Zuid-Holland, Utrecht en Flevoland. 
            Wij komen graag bij u langs voor een vrijblijvende offerte. Na het stukadoorswerk is uw 
            muur of plafond direct klaar om te schilderen of te behangen. Neem contact op en 
            ontdek wat wij voor u kunnen betekenen.
          </p>
        </div>
      }
    />
  );
}
